import { clonePlotFeature, createPlotFeature, } from "@plotlibre/core";
import { SelectionController, } from "./selection-controller.js";
/**
 * One history entry for a preflighted multi-feature document mutation.
 *
 * The command owns immutable before/after transactions and selection snapshots.
 * Redo reuses the exact captured feature revisions rather than regenerating
 * candidates. PlotStore listener failures are isolated after commit, so they
 * cannot prevent CommandHistory from recording this command.
 */
export class BatchEditCommand {
    label;
    #store;
    #selection;
    #executeTransaction;
    #undoTransaction;
    #beforeSelection;
    #afterSelection;
    #executionCount = 0;
    constructor(store, selection, options) {
        this.label = options.label ?? "batch-edit";
        this.#store = store;
        this.#selection = selection;
        this.#executeTransaction = cloneTransaction(options.execute);
        this.#undoTransaction = cloneTransaction(options.undo);
        this.#beforeSelection = cloneSelectionSnapshot(options.beforeSelection);
        this.#afterSelection = cloneSelectionSnapshot(options.afterSelection);
    }
    execute() {
        this.#selection.runWithoutStoreReconciliation(() => {
            this.#store.applyTransaction(this.#executeTransaction);
        });
        this.#selection.restore(this.#afterSelection, this.#executionCount === 0 ? "history-execute" : "history-redo");
        this.#executionCount += 1;
    }
    undo() {
        if (this.#executionCount === 0) {
            throw new Error("BatchEditCommand cannot undo before execute.");
        }
        this.#selection.runWithoutStoreReconciliation(() => {
            this.#store.applyTransaction(this.#undoTransaction);
        });
        this.#selection.restore(this.#beforeSelection, "history-undo");
    }
}
function cloneTransaction(transaction) {
    const additions = transaction.add?.map((feature) => createPlotFeature(feature));
    const replacements = transaction.replace?.map(clonePlotFeature);
    const removals = transaction.remove === undefined
        ? undefined
        : Object.freeze([...transaction.remove]);
    const orderedIds = transaction.orderedIds === undefined
        ? undefined
        : Object.freeze([...transaction.orderedIds]);
    return Object.freeze({
        ...(additions === undefined ? {} : { add: Object.freeze(additions) }),
        ...(replacements === undefined
            ? {}
            : { replace: Object.freeze(replacements) }),
        ...(removals === undefined ? {} : { remove: removals }),
        ...(orderedIds === undefined ? {} : { orderedIds }),
    });
}
function cloneSelectionSnapshot(snapshot) {
    const selectedIds = Object.freeze([...snapshot.selectedIds]);
    return Object.freeze({
        selectedIds,
        ...(snapshot.primaryId === undefined
            ? {}
            : { primaryId: snapshot.primaryId }),
        revision: snapshot.revision,
    });
}
//# sourceMappingURL=batch-edit-command.js.map